import axios from './axios';

const OWNER_EMAILS = new Set(['jackcarjunior@gmail.com', 'joaoprenzi@gmail.com']);

export function isPlatformOwner(user) {
  return OWNER_EMAILS.has(String(user?.email || user?.username || '').trim().toLowerCase());
}

export function ownerNavigation(config, user) {
  return config.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.ownerOnly || isPlatformOwner(user)),
  }));
}

export const OWNER_METRICS = [
  {key: 'registrations', label: 'Novos usuários', icon: 'eva:person-add-outline', description: 'Contas cadastradas no período.'},
  {key: 'activeUsers', label: 'Usuários ativos', icon: 'eva:activity-outline', description: 'Usuários com ações registradas no produto; cadastro ou login isolados não contam.'},
  {key: 'searchCampaigns', label: 'Campanhas de pesquisa', icon: 'eva:search-outline', description: 'Campanhas de pesquisa criadas no período.'},
  {key: 'outreachCampaigns', label: 'Campanhas de prospecção', icon: 'eva:paper-plane-outline', description: 'Campanhas de prospecção criadas no período.'},
  {key: 'emailsSent', label: 'E-mails enviados', icon: 'eva:email-outline', description: 'Envios de campanhas e da caixa de entrada, sem duplicar o mesmo envio.'},
  {key: 'whatsappSent', label: 'WhatsApp enviados', icon: 'mdi:whatsapp', description: 'Envios de campanhas e conversas manuais. Mensagens em fila não contam.'},
  {key: 'leadsConsumed', label: 'Leads consumidos', icon: 'eva:people-outline', description: 'Empresas adicionadas à base de cada usuário no período, contadas uma vez por registro.'},
  {key: 'enrichments', label: 'Enriquecimentos concluídos', icon: 'eva:flash-outline', description: 'Consultas concluídas, incluindo o histórico preservado, sem duplicar o job.'},
  {key: 'creditsConsumed', label: 'Créditos consumidos', icon: 'eva:credit-card-outline', description: 'Consumo efetivo de créditos regulares e promocionais. Reservas e reembolsos não contam.'},
  {key: 'onboarded', label: 'Onboardings concluídos', icon: 'eva:checkmark-circle-outline', description: 'Usuários que concluíram o onboarding no período, mesmo cadastrados antes.'},
  {key: 'searches', label: 'Pesquisas iniciadas', icon: 'eva:search-outline', description: 'Sessões de pesquisa guiada criadas, incluindo rascunhos.'},
  {key: 'contactsCreated', label: 'Contatos adicionados', icon: 'eva:person-outline', description: 'Contatos criados no período.'},
  {key: 'exports', label: 'Exportações concluídas', icon: 'eva:download-outline', description: 'Exportações de pesquisas guiadas com status concluído.'},
  {key: 'repliesReceived', label: 'Mensagens recebidas', icon: 'eva:message-circle-outline', description: 'Mensagens recebidas por e-mail e WhatsApp; não representa uma taxa de resposta a campanhas.'},
  {key: 'sendFailures', label: 'Falhas de envio em campanhas', icon: 'eva:alert-circle-outline', description: 'Mensagens de campanha com status atual de falha atualizado no período.'},
  {key: 'enrichmentFailed', label: 'Falhas de enriquecimento', icon: 'eva:alert-triangle-outline', description: 'Jobs retidos com falha ou tempo esgotado. Jobs antigos removidos não estão incluídos.'},
  {key: 'mailboxesConnected', label: 'Conexões de e-mail criadas', icon: 'eva:link-outline', description: 'Registros de conexão de e-mail criados no período; não indica conexão ativa.'},
  {key: 'whatsappConnected', label: 'Conexões WhatsApp criadas', icon: 'mdi:whatsapp', description: 'Registros de conexão WhatsApp criados no período; não indica conexão ativa.'},
];

export const metricValue = (data, key, period = 'current') => Number(data?.metrics?.[key]?.[period] ?? 0);

export function metricChange(current, previous) {
  if (!previous) return current ? 'Novo no período' : 'Sem alteração';
  const change = ((current - previous) / previous) * 100;
  return `${change > 0 ? '+' : ''}${change.toLocaleString('pt-BR', {maximumFractionDigits: 1})}%`;
}

export function analyticsDateRange(days = 30, now = new Date()) {
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  return {start: new Date(end.getTime() - (days - 1) * 86400000).toISOString().slice(0, 10), end: end.toISOString().slice(0, 10)};
}

export function validAnalyticsRange({start, end}, now = new Date()) {
  if (![start, end].every((value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))) return false;
  const dates = [start, end].map((value) => new Date(`${value}T00:00:00Z`));
  if (dates.some((date, index) => !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== [start, end][index])) return false;
  const days = (dates[1] - dates[0]) / 86400000 + 1;
  return days >= 1 && days <= 366 && end <= analyticsDateRange(1, now).end;
}

export async function fetchOwnerAnalytics({start, end, search = '', offset = 0}, signal) {
  const response = await axios.get('/api/owner-analytics', {params: {start, end, search, offset}, cache: false, signal});
  const data = response.data?.data ?? response.data;
  if (!data?.metrics || !data?.totals || !Array.isArray(data?.daily) || !Array.isArray(data?.users)) {
    throw new Error('Os dados de analytics estão indisponíveis. Tente novamente.');
  }
  return data;
}

export function ownerMetricsCsv(data) {
  const rows = [['Indicador', 'Período atual', 'Período anterior', 'Início UTC', 'Fim UTC (exclusivo)'],
    ...OWNER_METRICS.map(({key, label}) => [label, metricValue(data, key), metricValue(data, key, 'previous'), data.start, data.end])];
  return `\uFEFF${rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n')}`;
}
